import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

interface PINVerificationProps {
  deviceId: string;
  onSuccess: () => void;
  onRecovery: () => void;
}

const PINVerification: React.FC<PINVerificationProps> = ({ deviceId, onSuccess, onRecovery }) => {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState('');

  const hashPin = (pin: string) => {
    return btoa(pin);
  };

  const verifyPIN = async () => {
    if (pin.length !== 6) {
      setError(t('deviceLock.pinLength'));
      return;
    }

    try {
      const q = query(collection(db, 'devices'), where('deviceId', '==', deviceId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(t('deviceLock.deviceNotFound'));
        return;
      }

      const device = snapshot.docs[0].data();
      const hashedPin = hashPin(pin);

      if (device.pinHash === hashedPin) {
        setError('');
        setAttempts(0);
        onSuccess();
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        
        if (newAttempts >= 3) {
          setLocked(true);
          setError(t('deviceLock.tooManyAttempts'));
        } else {
          setError(t('deviceLock.incorrectPin', { remaining: 3 - newAttempts }));
        }
        setPin('');
      }
    } catch (error) {
      console.error('Error verifying PIN:', error);
      setError(t('deviceLock.verificationError'));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      verifyPIN();
    }
  };

  if (locked) {
    return (
      <div className="card-dark p-8 max-w-md mx-auto text-center">
        <div className="text-6xl mb-4">🔒</div>
        <h3 className="text-2xl font-bold text-white mb-4">{t('deviceLock.deviceLocked')}</h3>
        <p className="text-[#B8B8C3] mb-6">{t('deviceLock.deviceLockedDesc')}</p>
        <button onClick={onRecovery} className="btn-primary w-full">
          {t('deviceLock.useRecoveryCode')}
        </button>
      </div>
    );
  }

  return (
    <div className="card-dark p-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="text-5xl mb-4">🔐</div>
        <h3 className="text-2xl font-bold text-white mb-2">{t('deviceLock.enterPIN')}</h3>
        <p className="text-[#B8B8C3]">{t('deviceLock.enterPINDesc')}</p>
      </div>

      <div className="space-y-4">
        <div>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyPress={handleKeyPress}
            className="input-dark w-full text-center text-3xl tracking-widest"
            placeholder="••••••"
            maxLength={6}
            autoFocus
          />
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        <button
          onClick={verifyPIN}
          disabled={pin.length !== 6}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('deviceLock.verify')}
        </button>

        <button
          onClick={onRecovery}
          className="w-full text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          {t('deviceLock.forgotPIN')}
        </button>

        {attempts > 0 && (
          <p className="text-xs text-yellow-400 text-center">
            {t('deviceLock.attemptsRemaining', { count: 3 - attempts })}
          </p>
        )}
      </div>
    </div>
  );
};

export default PINVerification;
